import React from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack,
  InputAdornment,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  OutlinedInput,
  SelectChangeEvent,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  PushPin as PinIcon,
  Archive as ArchiveIcon,
} from '@mui/icons-material';
import type { NoteFilters as NoteFiltersType, NoteType } from '../../types/note';
import { NOTE_TYPE_OPTIONS } from '../../types/note';

interface NoteFiltersProps {
  filters: NoteFiltersType;
  onFiltersChange: (filters: NoteFiltersType) => void;
  onSearch: (query: string) => void;
  searchQuery: string;
}

export const NoteFilters: React.FC<NoteFiltersProps> = ({
  filters,
  onFiltersChange,
  onSearch,
  searchQuery,
}) => {
  const handleTypeChange = (event: SelectChangeEvent<NoteType[]>) => {
    const value = event.target.value as NoteType[];
    onFiltersChange({ ...filters, type: value, page: 1 });
  };

  const handleSortChange = (event: SelectChangeEvent<string>) => {
    onFiltersChange({
      ...filters,
      sortBy: event.target.value as NoteFiltersType['sortBy'],
      page: 1,
    });
  };

  const handleArchivedToggle = (
    _: React.MouseEvent<HTMLElement>,
    newValue: string | null
  ) => {
    let archived: boolean | 'all' | undefined;
    if (newValue === 'archived') {
      archived = true;
    } else if (newValue === 'all') {
      archived = 'all';
    } else {
      archived = false;
    }
    onFiltersChange({ ...filters, archived, page: 1 });
  };

  const handlePinnedToggle = () => {
    onFiltersChange({
      ...filters,
      pinned: !filters.pinned,
      page: 1,
    });
  };

  const handleClearSearch = () => {
    onSearch('');
  };

  const handleClearFilters = () => {
    onFiltersChange({
      page: 1,
      limit: filters.limit,
      archived: false,
    });
    onSearch('');
  };

  const currentArchiveValue = filters.archived === true
    ? 'archived'
    : filters.archived === 'all'
    ? 'all'
    : 'active';

  const hasActiveFilters =
    searchQuery ||
    (filters.type && filters.type.length > 0) ||
    filters.pinned ||
    filters.archived === true ||
    filters.archived === 'all';

  return (
    <Box sx={{ mb: 3 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
        {/* Search */}
        <TextField
          placeholder="Search notes..."
          size="small"
          value={searchQuery}
          onChange={(e) => onSearch(e.target.value)}
          sx={{ minWidth: 200, flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClearSearch}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        {/* Type Filter */}
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Type</InputLabel>
          <Select
            multiple
            value={filters.type || []}
            onChange={handleTypeChange}
            input={<OutlinedInput label="Type" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => {
                  const option = NOTE_TYPE_OPTIONS.find((o) => o.value === value);
                  return (
                    <Chip
                      key={value}
                      label={`${option?.icon || ''} ${option?.label || value}`}
                      size="small"
                    />
                  );
                })}
              </Box>
            )}
          >
            {NOTE_TYPE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.icon} {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Sort */}
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Sort By</InputLabel>
          <Select
            value={filters.sortBy || 'createdAt'}
            onChange={handleSortChange}
            label="Sort By"
          >
            <MenuItem value="createdAt">Created</MenuItem>
            <MenuItem value="updatedAt">Updated</MenuItem>
            <MenuItem value="title">Title</MenuItem>
          </Select>
        </FormControl>

        {/* Pinned Toggle */}
        <ToggleButton
          value="pinned"
          selected={!!filters.pinned}
          onChange={handlePinnedToggle}
          size="small"
          sx={{ px: 2 }}
        >
          <PinIcon fontSize="small" sx={{ mr: 0.5 }} />
          Pinned
        </ToggleButton>

        {/* Archive Toggle */}
        <ToggleButtonGroup
          value={currentArchiveValue}
          exclusive
          onChange={handleArchivedToggle}
          size="small"
        >
          <ToggleButton value="active">Active</ToggleButton>
          <ToggleButton value="archived">
            <ArchiveIcon fontSize="small" sx={{ mr: 0.5 }} />
            Archived
          </ToggleButton>
          <ToggleButton value="all">All</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {/* Clear Filters */}
      {hasActiveFilters && (
        <Chip
          label="Clear all filters"
          onDelete={handleClearFilters}
          size="small"
          color="primary"
          variant="outlined"
        />
      )}
    </Box>
  );
};

export default NoteFilters;
